import { FC, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "react-beautiful-dnd"
import { GripVertical, ChevronRight } from "lucide-react"

import {
  If,
  LoanProgramDetailsHeader,
  PricingProfileDetails
} from "../../common/ui"
import { PrimaryBtn } from "../../common/ui/buttons"
import {
  useFetchPricingSetDetailsQuery,
  useUpdatePricingSetProfileOrderMutation
} from "../../redux/queries"
import { useToast } from "../../common/hooks"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { formatAsTwoDigits } from "../../common/utils"
import { PricingProfile } from "../../types"

const reorder = (
  list: PricingProfile[],
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const LoanProgramDetailsPage: FC = () => {
  const { id } = useParams()
  const { pushSuccessToast, pushErrorToast } = useToast()

  const [selectedItem, setSelectedItem] = useState<PricingProfile | undefined>(
    undefined
  )
  const [orderedProfiles, setOrderedProfiles] = useState<PricingProfile[]>([])

  const { data: pricingSet, isLoading } = useFetchPricingSetDetailsQuery(
    { id: id ?? "" },
    { refetchOnMountOrArgChange: true }
  )

  const [updateOrder, { isLoading: isSaving }] =
    useUpdatePricingSetProfileOrderMutation()

  // Sync local order from fetched data
  useEffect(() => {
    if (pricingSet?.pricing_profiles) {
      setOrderedProfiles(pricingSet.pricing_profiles)
    }
  }, [pricingSet?.pricing_profiles])

  const hasOrderChanged = useMemo(() => {
    const original = pricingSet?.pricing_profiles ?? []
    if (original.length !== orderedProfiles.length) return false
    return orderedProfiles.some(
      (p, i) => p.pricing_profile_id !== original[i]?.pricing_profile_id
    )
  }, [pricingSet?.pricing_profiles, orderedProfiles])

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    setOrderedProfiles(prev =>
      reorder(prev, result.source.index, result.destination!.index)
    )
  }

  const handleSave = async () => {
    if (!id) return
    try {
      await updateOrder({
        pricing_set_id: id,
        new_weights: orderedProfiles.map(p => ({
          wid: p.id,
          ppid: p.pricing_profile_id
        }))
      }).unwrap()
      pushSuccessToast("Profile order updated")
    } catch {
      pushErrorToast("Failed to update profile order")
    }
  }

  const handleReset = () => {
    if (pricingSet?.pricing_profiles) {
      setOrderedProfiles(pricingSet.pricing_profiles)
    }
  }

  const handleSelectItem = (profile: PricingProfile) => {
    setSelectedItem(profile)
  }

  const handleClose = () => setSelectedItem(undefined)

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["loan-programs"]}>
      <If
        isLoading={isLoading}
        condition={!!pricingSet}
        then={
          <>
            <div className="h-full flex flex-col">
              <div className="pt-6 bg-white">
                <div className="px-6">
                  <LoanProgramDetailsHeader pricingSet={pricingSet!} />
                </div>
              </div>
              <div className="p-6 flex-1 bg-white">
                {hasOrderChanged && !isSaving && (
                  <div className="mb-4 flex items-center gap-3">
                    <PrimaryBtn onClick={handleSave}>Save order</PrimaryBtn>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Reset
                    </button>
                  </div>
                )}
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="pricing-profiles">
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="border rounded-lg divide-y divide-gray-200"
                      >
                        {orderedProfiles.map((profile, index) => (
                          <Draggable
                            key={profile.id}
                            draggableId={profile.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`flex items-center justify-between px-4 py-4 ${
                                  snapshot.isDragging
                                    ? "bg-gray-50 shadow-md rounded-lg"
                                    : "bg-white"
                                }`}
                              >
                                <div className="flex items-center">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="flex items-center cursor-grab active:cursor-grabbing"
                                  >
                                    <GripVertical className="text-neutral-400" />
                                    <div className="text-2xl font-extrabold ml-2">
                                      {formatAsTwoDigits(index + 1)}
                                    </div>
                                  </div>
                                  <div className="pl-5 font-bold">
                                    {profile.title}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleSelectItem(profile)}
                                  className="text-neutral-400 hover:text-neutral-600 p-1"
                                  aria-label={`View ${profile.title}`}
                                >
                                  <ChevronRight />
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                {!isLoading && orderedProfiles.length === 0 && (
                  <div className="text-center text-neutral-400 py-12">
                    No pricing profiles in this set.
                  </div>
                )}
              </div>
            </div>

            <PricingProfileDetails
              pricingProfile={selectedItem}
              onClose={handleClose}
            />
          </>
        }
        else={
          <div className="p-6">
            Could not fetch details of this loan program.
          </div>
        }
      />
    </RequireRole>
  )
}

export default LoanProgramDetailsPage
